import { Control, FieldErrors, useController, UseFormSetValue } from 'react-hook-form'
import React, { useEffect, useState } from 'react'
import { format, endOfDay, startOfDay, parse, isSameDay } from 'date-fns'
import { getTimePeriodNextMonth, getTimePeriodThisMonth } from '../../../../common/helpers/date'
import { List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import { msgid, ngettext, t } from 'ttag'
import { calculateDuration, FormData } from '../../ChallengeAdmin'
import Typography from '@material-ui/core/Typography'

import { ReactComponent as DateNextMonthActiveIcon } from '../../../../assets/v3/icons/date-next-month-active.svg'
import { ReactComponent as DateNextMonthInactiveIcon } from '../../../../assets/v3/icons/date-next-month-inactive.svg'
import { ReactComponent as DateThisMonthActiveIcon } from '../../../../assets/v3/icons/date-this-month-active.svg'
import { ReactComponent as DateThisMonthInactiveIcon } from '../../../../assets/v3/icons/date-this-month-inactive.svg'

import { ReactComponent as DateActiveIcon } from '../../../../assets/v3/icons/date-active.svg'
import { ReactComponent as DateInactiveIcon } from '../../../../assets/v3/icons/date-inactive.svg'
import YumField from '../../../../components/YumInput/YumInput'
import { red } from '../../../../styles/material-ui-theme-v2'

interface DateRangeInputProps {
  control: Control<FormData>
  rules: any
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  isUserCreatedChallenge: boolean
  salespersonLink: any
  contractStartDate: Date | null
  contractEndDate: Date | null
  contractPeriod: any
  plan: string
}

export default function DateRangeInput({
  control,
  rules,
  setValue,
  errors,
  isUserCreatedChallenge,
  salespersonLink,
  contractStartDate,
  contractEndDate,
  contractPeriod,
  plan,
}: DateRangeInputProps) {
  const { field } = useController({ control, name: 'dateRange', rules })

  const [durationDays, setDurationDays] = useState(calculateDuration(field.value[0], field.value[1]))

  const { startDate: startDateThisMonth, endDate: endDateThisMonth } = getTimePeriodThisMonth()
  const { startDate: startDateNextMonth, endDate: endDateNextMonth } = getTimePeriodNextMonth()

  useEffect(() => {
    setValue('days', durationDays)
  }, [durationDays])

  let nextMonthSelected = false
  let thisMonthSelected = false
  let contractPeriodSelected = false
  if (field.value[0] && field.value[1]) {
    nextMonthSelected = isSameDay(startDateNextMonth, field.value[0]) && isSameDay(endDateNextMonth, field.value[1])
    thisMonthSelected = isSameDay(startDateThisMonth, field.value[0]) && isSameDay(endDateThisMonth, field.value[1])
    contractPeriodSelected =
      contractStartDate && contractEndDate
        ? isSameDay(contractStartDate, field.value[0]) && isSameDay(contractEndDate, field.value[1])
        : false
  }

  const setContractPeriodDates = () => {
    if (contractStartDate && contractEndDate) {
      const startDate = contractStartDate
      const endDate = endOfDay(contractEndDate)
      field.onChange([startDate, endDate])
      const days = calculateDuration(startDate, endDate)
      setDurationDays(days)
    }
  }

  // Logic if we have contract period.
  let showContractPeriodOption = false
  if (contractStartDate && contractEndDate) {
    const contractDays = calculateDuration(contractStartDate, contractEndDate)
    const contractMoreThanMonth = contractDays > 31
    const contractEnded = contractEndDate < new Date()

    // Don't show "This month" and "Last month" options if contract period is short and has not ended.
    if (!contractMoreThanMonth && !contractEnded) {
      showContractPeriodOption = true
    }
  }

  const setThisMonthDates = () => {
    const startDate = new Date(startDateThisMonth)
    const endDate = new Date(endDateThisMonth)

    field.onChange([startDate, endDate])
    const days = calculateDuration(startDate, endDate)
    setDurationDays(days)
  }

  const setNextMonthDates = () => {
    const startDate = new Date(startDateNextMonth)
    const endDate = new Date(endDateNextMonth)

    field.onChange([startDate, endDate])
    const days = calculateDuration(startDate, endDate)
    setDurationDays(days)
  }

  const prepareStartDate = (newStartDate: Date) => {
    newStartDate = startOfDay(newStartDate)
    return newStartDate
  }

  const handleStartDateChange = (event: { target: { value: string } }) => {
    const value = event.target.value
    const endDate = field.value[1]

    let newStartDate = startDateNextMonth
    if (value) {
      newStartDate = parse(value, 'yyyy-MM-dd', new Date())
      newStartDate = prepareStartDate(newStartDate)
    }

    field.onChange([newStartDate, endDate])

    const days = calculateDuration(newStartDate, endDate)
    setDurationDays(days)
  }

  const prepareEndDate = (newEndDate: Date) => {
    newEndDate = endOfDay(newEndDate)
    return newEndDate
  }

  const handleEndDateChange = (event: { target: { value: string } }) => {
    const value = event.target.value
    const startDate = field.value[0]

    let newEndDate = endDateNextMonth
    if (value) {
      newEndDate = parse(value, 'yyyy-MM-dd', new Date())
      newEndDate = prepareEndDate(newEndDate)
    }

    field.onChange([startDate, newEndDate])

    const days = calculateDuration(startDate, newEndDate)
    setDurationDays(days)
  }

  return (
    <>
      <List style={{ marginBottom: '24px' }}>
        {showContractPeriodOption ? (
          <ListItem
            role={undefined}
            button
            disableRipple
            onClick={setContractPeriodDates}
            selected={contractPeriodSelected}
          >
            <ListItemIcon>
              {contractPeriodSelected ? <DateThisMonthActiveIcon /> : <DateThisMonthInactiveIcon />}
            </ListItemIcon>
            <ListItemText id="contractPeriod" primary={plan === 'trial' ? t`Trial period` : t`Contract period`} />
          </ListItem>
        ) : (
          <>
            <ListItem role={undefined} button disableRipple onClick={setNextMonthDates} selected={nextMonthSelected}>
              <ListItemIcon>
                {nextMonthSelected ? <DateNextMonthActiveIcon /> : <DateNextMonthInactiveIcon />}
              </ListItemIcon>
              <ListItemText id="nextMonth" primary={t`Next month`} />
            </ListItem>
            <ListItem role={undefined} button disableRipple onClick={setThisMonthDates} selected={thisMonthSelected}>
              <ListItemIcon>
                {thisMonthSelected ? <DateThisMonthActiveIcon /> : <DateThisMonthInactiveIcon />}
              </ListItemIcon>
              <ListItemText id="thisMonth" primary={t`This month`} />
            </ListItem>
          </>
        )}
      </List>
      <Typography variant="subtitle2" style={{ marginBottom: 16 }}>
        {t`Custom period`}
      </Typography>
      <div className="date-range" style={{ display: 'flex', gap: 15 }}>
        <YumField
          type="date"
          label={t`Start date`}
          startIcon={DateActiveIcon}
          startIconInactive={DateInactiveIcon}
          value={field.value[0] && format(field.value[0], 'yyyy-MM-dd')}
          onChange={handleStartDateChange}
          error={!field.value[0] || (!!errors.dateRange && errors.dateRange.type === 'startDate')}
        />
        <YumField
          type="date"
          label={t`End date`}
          variant="filled"
          startIcon={DateActiveIcon}
          startIconInactive={DateInactiveIcon}
          value={field.value[1] && format(field.value[1], 'yyyy-MM-dd')}
          onChange={handleEndDateChange}
          error={!!errors.dateRange && errors.dateRange.type !== 'startDate'}
        />
      </div>
      {errors.dateRange ? (
        <>
          <div style={{ marginTop: '20px', textAlign: 'center', color: red }}>{errors.dateRange?.message}</div>
          {['startDate', 'endDate'].includes(errors.dateRange.type as string) && (
            <div style={{ marginTop: '10px', textAlign: 'center', lineHeight: '1.5' }}>
              {contractPeriod && <div style={{ marginBottom: '10px' }}>{contractPeriod}</div>}
              {isUserCreatedChallenge ? (
                <div>{t`Please inform your admin that you are interested in extending YuMuuv’s usage period.`}</div>
              ) : (
                <>
                  <div>{t`To extend your platform access, please reach out to your dedicated sales manager at YuMuuv.`}</div>
                  {salespersonLink}
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {durationDays > 0 && (
            <div style={{ marginTop: 20, textAlign: 'center' }}>
              {t`Your challenge will last`}:{' '}
              <strong>
                {durationDays} {ngettext(msgid`day`, `days`, durationDays)}
              </strong>
            </div>
          )}
        </>
      )}
    </>
  )
}
